import { Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { SessionView } from '../../shared/api';
import { APP_NAME } from '../../shared/brand';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import { qk, useApplySession } from '../api/hooks';
import { BrandMark } from '../components/BrandMark';

interface GsiApi {
  initialize(opts: {
    client_id: string;
    nonce?: string;
    callback: (response: { credential?: string }) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    use_fedcm_for_button?: boolean;
  }): void;
  renderButton(el: HTMLElement, opts: Record<string, string | number>): void;
  disableAutoSelect(): void;
}

function gsi(): GsiApi | undefined {
  return (window as unknown as { google?: { accounts?: { id?: GsiApi } } }).google?.accounts?.id;
}

let gsiScript: Promise<void> | null = null;
/** Google Identity Services, loaded only on the sign-in screen. */
function loadGsi(): Promise<void> {
  gsiScript ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiScript = null;
      reject(new Error('Google sign-in could not load'));
    };
    document.head.appendChild(script);
  });
  return gsiScript;
}

/** Stop Google from signing straight back in after "Sign out". */
export function forgetGoogleAutoSelect(): void {
  gsi()?.disableAutoSelect();
}

function isDark(): boolean {
  const pref = document.documentElement.getAttribute('data-theme');
  return pref ? pref === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

function GoogleButton({
  clientId,
  nonce,
  onSession,
  onError,
}: {
  clientId: string;
  nonce: string | undefined;
  onSession: (s: SessionView) => void;
  onError: (m: string | null) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const handlers = useRef({ onSession, onError });
  handlers.current = { onSession, onError };
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGsi()
      .then(() => {
        const id = gsi();
        if (cancelled || !ref.current || !id) return;
        id.initialize({
          client_id: clientId,
          nonce,
          ux_mode: 'popup',
          use_fedcm_for_button: true,
          callback: ({ credential }) => {
            if (!credential) return;
            handlers.current.onError(null);
            api<SessionView>('/auth/google', { method: 'POST', body: { credential } })
              .then((s) => handlers.current.onSession(s))
              .catch((err) => handlers.current.onError(message(err, 'Google sign-in failed. Try again.')));
          },
        });
        id.renderButton(ref.current, {
          type: 'standard',
          theme: isDark() ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: Math.min(360, ref.current.clientWidth || 320),
        });
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [clientId, nonce]);

  if (failed) return <p className="small muted">Google sign-in couldn't load. Check your connection, or use an email code.</p>;
  return <div ref={ref} className="signin__google" />;
}

function EmailCode({ onSession }: { onSession: (s: SessionView) => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  const send = async (to: string) => {
    setBusy(true);
    setError(null);
    try {
      await api('/auth/email/start', { method: 'POST', body: { email: to } });
      setSentTo(to);
      setCode('');
      setWait(60);
      window.setTimeout(() => codeRef.current?.focus(), 0);
    } catch (err) {
      setError(message(err, "The code couldn't be sent. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!sentTo) return;
    setBusy(true);
    setError(null);
    try {
      onSession(await api<SessionView>('/auth/email/verify', { method: 'POST', body: { email: sentTo, code } }));
    } catch (err) {
      setError(message(err, "That code didn't work."));
      setBusy(false);
    }
  };

  if (!sentTo) {
    return (
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) void send(email.trim());
        }}
      >
        <label className="field">
          <span className="field__label">Email</span>
          <input className="input" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <button className="btn btn--primary btn--block" disabled={busy || !email.trim()}>
          <Mail size={17} /> Email me a code
        </button>
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
      </form>
    );
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (/^\d{6}$/.test(code)) void verify();
      }}
    >
      <p className="small muted">
        If <b>{sentTo}</b> can sign in, a 6-digit code is on its way. It expires in 10 minutes.
      </p>
      <label className="field">
        <span className="field__label">Code</span>
        <input
          ref={codeRef}
          className="input num signin__code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </label>
      <button className="btn btn--primary btn--block" disabled={busy || code.length !== 6}>
        Sign in
      </button>
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
      <div className="row between">
        <button type="button" className="btn btn--quiet btn--sm" onClick={() => setSentTo(null)}>
          Use a different email
        </button>
        <button type="button" className="btn btn--quiet btn--sm" disabled={busy || wait > 0} onClick={() => void send(sentTo)}>
          {wait > 0 ? `Send a new code (${wait}s)` : 'Send a new code'}
        </button>
      </div>
    </form>
  );
}

export function SignInPage({ session }: { session: SessionView }) {
  const apply = useApplySession();
  const client = useQueryClient();
  const [googleError, setGoogleError] = useState<string | null>(null);
  const onGoogleError = (m: string | null) => {
    setGoogleError(m);
    // Each nonce works once: get a fresh one so the next try can succeed.
    if (m) void client.invalidateQueries({ queryKey: qk.session });
  };

  return (
    <main className="signin">
      <div className="card card--pad stack signin__card">
        <div className="row" style={{ gap: 12 }}>
          <BrandMark />
          <div>
            <h1 className="section-title">{APP_NAME}</h1>
            <p className="small muted">Sign in to your food and weight log.</p>
          </div>
        </div>
        {session.google && <GoogleButton clientId={session.google.clientId} nonce={session.google.nonce} onSession={apply} onError={onGoogleError} />}
        {googleError && (
          <p className="field__error" role="alert">
            {googleError}
          </p>
        )}
        {session.google && session.emailCodes && <div className="signin__or">or</div>}
        {session.emailCodes && <EmailCode onSession={apply} />}
        {!session.google && !session.emailCodes && <p className="small muted">Sign-in isn't set up on this server yet.</p>}
      </div>
    </main>
  );
}
